/**
 * Compute backend `is_custom` / `override_type` for a self-schedule
 * slot at payload-build time.
 *
 * Why this lives here (not in state):
 *   - Avoids per-tick recompute and state churn during drag/edit.
 *   - Save-time inputs (busy events + per-interviewer working hours)
 *     are already snapshotted into `payloadRef.current.details` by
 *     `DetailsStep.syncToPayload`, so the mapper has everything in
 *     one pure call.
 *
 * Why we read raw per-interviewer working hours (not common
 * availability): legacy `KInterviewScheduler` uses each interviewer's
 * own schedule and applies union (1:1) or intersection (panel) at
 * classification time — see `useSchedulerDetailsPayload.tsx#185-215`.
 * The new wizard's `availability` prop is already common AND has busy
 * subtracted, so we can't recover the "outside any interviewer's
 * working hours" signal from it. The raw map preserves that detail.
 *
 * Backend semantics — `core/app/models/ats_schedule_slot.rb#47-62`:
 *
 *   | override_type            | Filter behaviour                      |
 *   | ------------------------ | ------------------------------------- |
 *   | `none`                   | Strict working-hours + calendar       |
 *   | `calendar`               | Skip calendar (busy) filter           |
 *   | `working_hours`          | Skip working-hours filter             |
 *   | `calendar_working_hours` | Skip both                             |
 *
 *   Plus a backward-compat default: `is_custom=true && override_type=none`
 *   resolves to `effective="calendar_working_hours"` server-side. We
 *   don't rely on that; we always send the explicit type.
 */
import type { BusyEvent, Interval, InterviewKind } from '../availability';
import type { SlotOverrideType } from '../calendar/selfSchedule/types';

/** Per-interviewer working-hours map, keyed by stringified user id. */
export type WorkingHoursByInterviewer = Record<string, Interval[]>;

export interface ComputeSlotOverrideArgs {
  slotStart: string;
  slotEnd: string;
  busyEvents: BusyEvent[];
  workingHoursByInterviewer: WorkingHoursByInterviewer;
  interviewerIds: ReadonlyArray<string | number>;
  interviewKind: InterviewKind;
}

export interface SlotOverrideResult {
  is_custom: boolean;
  override_type: SlotOverrideType;
}

const NONE: SlotOverrideResult = { is_custom: false, override_type: 'none' };

/**
 * `true` when `[startMs, endMs)` intersects any busy event's interval.
 * Standard half-open interval test.
 */
function overlapsAnyBusy(
  startMs: number,
  endMs: number,
  busyEvents: BusyEvent[]
): boolean {
  for (const evt of busyEvents) {
    const evtStart = Date.parse(evt.start);
    const evtEnd = Date.parse(evt.end);
    if (!Number.isFinite(evtStart) || !Number.isFinite(evtEnd)) continue;
    if (startMs < evtEnd && endMs > evtStart) return true;
  }
  return false;
}

/**
 * `true` when the slot is fully contained in at least one of the
 * interviewer's working-hours windows. Mirrors legacy
 * `isSlotWithinInterviewerAvailability`.
 */
function slotFullyInsideAnyInterval(
  startMs: number,
  endMs: number,
  intervals: Interval[]
): boolean {
  for (const win of intervals) {
    const winStart = Date.parse(win.start);
    const winEnd = Date.parse(win.end);
    if (!Number.isFinite(winStart) || !Number.isFinite(winEnd)) continue;
    if (startMs >= winStart && endMs <= winEnd) return true;
  }
  return false;
}

/**
 * `true` when the slot falls outside the interviewers' working hours
 * per the kind's aggregation rule:
 *
 *   - `one_on_one` → outside iff NO interviewer has the slot inside
 *     their own working hours (union semantics — at least one is
 *     enough to count as "inside").
 *   - `panel`      → outside iff ANY interviewer doesn't have the slot
 *     inside their working hours (intersection semantics — all must
 *     be inside).
 *
 * If we have no working-hours data for any selected interviewer we
 * return `false` (treat as "in-hours" — no signal, no override). This
 * matches legacy: empty `availabilityByInterviewer[id]` causes
 * `isSlotWithinInterviewerAvailability` to return false, but the
 * legacy aggregate logic only fires the override when ALL evidence is
 * negative — partial evidence shouldn't flag a slot. Same rationale
 * here: don't fabricate an override from missing data.
 */
function isOutsideWorkingHours(
  startMs: number,
  endMs: number,
  workingHoursByInterviewer: WorkingHoursByInterviewer,
  interviewerIds: ReadonlyArray<string | number>,
  interviewKind: InterviewKind
): boolean {
  if (interviewerIds.length === 0) return false;
  const idsWithSchedule = interviewerIds.filter((id) => {
    const intervals = workingHoursByInterviewer[String(id)];
    return Array.isArray(intervals) && intervals.length > 0;
  });
  if (idsWithSchedule.length === 0) return false;

  if (interviewKind === 'one_on_one') {
    // Union — outside only if NOBODY has it inside.
    const anyInside = idsWithSchedule.some((id) =>
      slotFullyInsideAnyInterval(
        startMs,
        endMs,
        workingHoursByInterviewer[String(id)] ?? []
      )
    );
    return !anyInside;
  }

  // Panel — intersection. Outside if ANY of the scheduled interviewers
  // doesn't have it inside their hours. Interviewers without schedule
  // data are skipped (same as legacy — no data, no veto).
  const allInside = idsWithSchedule.every((id) =>
    slotFullyInsideAnyInterval(
      startMs,
      endMs,
      workingHoursByInterviewer[String(id)] ?? []
    )
  );
  return !allInside;
}

/**
 * Maps `(overlapsBusy, outsideWorkingHours)` → `override_type`.
 * Mirrors legacy `getOverrideType` 1:1.
 */
function combine(
  overlapsBusy: boolean,
  outsideHours: boolean
): SlotOverrideType {
  if (overlapsBusy && outsideHours) return 'calendar_working_hours';
  if (overlapsBusy) return 'calendar';
  if (outsideHours) return 'working_hours';
  return 'none';
}

/**
 * Pure compute. Given a slot + the snapshot of busy events + raw
 * per-interviewer working hours, return `{ is_custom, override_type }`
 * to send to the backend.
 *
 *   - `none`              → `{ is_custom: false, override_type: 'none' }`
 *   - any non-`none` type → `{ is_custom: true,  override_type: <type> }`
 *
 * `is_custom: true` mirrors legacy: any override-bearing slot was a
 * recruiter override. Leaving `is_custom` aligned with the type avoids
 * the `is_custom=true / override_type=none` backward-compat default
 * from the backend ever activating, which would silently expand the
 * skip set to `calendar_working_hours` — see `effective_override_type`
 * in `ats_schedule_slot.rb`.
 */
export function computeSlotOverride({
  slotStart,
  slotEnd,
  busyEvents,
  workingHoursByInterviewer,
  interviewerIds,
  interviewKind,
}: ComputeSlotOverrideArgs): SlotOverrideResult {
  const startMs = Date.parse(slotStart);
  const endMs = Date.parse(slotEnd);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return NONE;

  const overlapsBusy = overlapsAnyBusy(startMs, endMs, busyEvents);
  const outsideHours = isOutsideWorkingHours(
    startMs,
    endMs,
    workingHoursByInterviewer,
    interviewerIds,
    interviewKind
  );
  const override_type = combine(overlapsBusy, outsideHours);
  if (override_type === 'none') return NONE;
  return { is_custom: true, override_type };
}
